import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Message {
  id: string;
  conversation_id: string;
  direction: 'inbound' | 'outbound';
  provider_message_id: string | null;
  type: string;
  body: string | null;
  media_url: string | null;
  mime_type: string | null;
  transcricao: string | null;
  status: string | null;
  erro_codigo: string | null;
  erro_detalhe: string | null;
  message_source: string | null;
  is_private_note: boolean;
  sent_by_user_id: string | null;
  created_at: string;
}

const PAGE_SIZE = 50;

// A ORDEM DA MÁQUINA PARA A IA NÃO APARECE NA CONVERSA (Douglas 16/10/2026: "não quero este
// monte de nota interna nas conversas... tudo muito repetitivo"). Ronda do ciclo, revisão,
// volta de 6 h e refazer/confirmar o fechamento são a instrução que a IA lê; ficam no banco
// para ela e para auditoria, mas quem atende não precisa ver. Nota de gente aparece sempre.
const ORDEM_DA_MAQUINA = /^\s*(\[(RONDA DO CICLO|REVIS[ÃA]O DAS \d{1,2}H|VOLTA DE 6 H)\]|REFAZER O FECHAMENTO|CONFIRMAR A PROPOSTA ACEITA)/i;

export function ehOrdemDaMaquina(m: Pick<Message, 'is_private_note' | 'sent_by_user_id' | 'body'>): boolean {
  return !!m.is_private_note && !m.sent_by_user_id && ORDEM_DA_MAQUINA.test(String(m.body ?? ''));
}

export function useMessages(conversationId: string | null, targetMessageId: string | null = null, navigationKey?: string) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [targetError, setTargetError] = useState<string | null>(null);
  const oldestCreatedAt = useRef<string | null>(null);
  const newestCreatedAt = useRef<string | null>(null);

  // Carrega mensagens iniciais (últimas 50)
  useEffect(() => {
    let cancelled = false;
    setTargetError(null);
    if (!conversationId) {
      setMessages([]);
      setHasMore(false);
      return;
    }

    setIsLoading(true);
    setMessages([]);
    oldestCreatedAt.current = null;
    newestCreatedAt.current = null;

    // Notas internas (is_private_note=true) APARECEM na timeline pra quem
    // trabalha no CRM — ChatWindow já renderiza com fundo vermelho e cadeado.
    // O que NÃO sai é pro cliente (não tem WhatsApp do cliente lendo isso).
    // Antes filtrava .eq('is_private_note', false) e notas de transferência
    // ficavam invisíveis até dar F5 (realtime push pegava, load inicial não).
    const carregar = async () => {
        const { data, error } = await supabase
          .from('whatsapp_messages')
          .select('*')
          .eq('conversation_id', conversationId)
          .order('created_at', { ascending: false })
          .limit(PAGE_SIZE);
        if (cancelled) return;
        if (error) {
          console.error(error);
          if (targetMessageId) setTargetError('Não foi possível carregar a conversa. Tente abrir a mensagem novamente.');
          setIsLoading(false);
          return;
        }
        let msgs = [...(data || [])].reverse() as Message[];
        let more = (data?.length || 0) === PAGE_SIZE;
        if (targetMessageId && !msgs.some(m => m.id === targetMessageId)) {
          try {
            // Busca autenticada e restrita à conversa: nunca confundir ID do
            // provedor com o UUID da mensagem original salvo na proposta.
            const target = await supabase.from('whatsapp_messages').select('*')
              .eq('conversation_id', conversationId).eq('id', targetMessageId).maybeSingle();
            if (cancelled) return;
            if (target.error || !target.data) throw new Error('Mensagem original não localizada ou sem acesso.');
            const history: Message[] = [];
            // Carrega o intervalo contínuo até a mensagem, sem buracos no chat.
            // Limite explícito protege o navegador de históricos excepcionais.
            for (let page = 0; page < 20; page++) {
              const result = await supabase.from('whatsapp_messages').select('*')
                .eq('conversation_id', conversationId).gte('created_at', target.data.created_at)
                .order('created_at', { ascending: false }).order('id', { ascending: false })
                .range(page * 500, (page + 1) * 500 - 1);
              if (cancelled) return;
              if (result.error) throw new Error('Não foi possível carregar o histórico. Tente abrir a mensagem novamente.');
              history.push(...(result.data || []) as Message[]);
              if (history.some(m => m.id === targetMessageId)) {
                msgs = [...new Map(history.map(m => [m.id, m])).values()].reverse();
                more = true;
                break;
              }
              if ((result.data?.length || 0) < 500 || page === 19) {
                throw new Error('Não foi possível alcançar a mensagem neste histórico. Use Carregar mensagens anteriores.');
              }
            }
          } catch (err) {
            if (cancelled) return;
            setTargetError(err instanceof Error ? err.message : 'Não foi possível abrir a mensagem original.');
          }
        }
        if (cancelled) return;
        setMessages(msgs);
        setHasMore(more);
        if (msgs.length > 0) {
          oldestCreatedAt.current = msgs[0].created_at;
          newestCreatedAt.current = msgs[msgs.length - 1].created_at;
        }
        setIsLoading(false);
    };
    void carregar();
    return () => { cancelled = true; };
  }, [conversationId, targetMessageId, navigationKey]);

  // ⚡ Polling removido — Realtime abaixo já cobre INSERT/UPDATE/DELETE em tempo real.
  // Antes rodava polling A CADA 60s + Realtime ao mesmo tempo (trabalho duplicado).

  // Carregar mensagens anteriores (load more no topo)
  const loadMore = async () => {
    if (!conversationId || !oldestCreatedAt.current || !hasMore) return;

    const { data, error } = await supabase
      .from('whatsapp_messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .lt('created_at', oldestCreatedAt.current)
      .order('created_at', { ascending: false })
      .limit(PAGE_SIZE);

    if (error || !data) return;

    const older = (data || []).reverse() as Message[];
    setMessages((prev) => [...older, ...prev]);
    setHasMore(data.length === PAGE_SIZE);
    if (older.length > 0) oldestCreatedAt.current = older[0].created_at;
  };

  const sincronizarRef = useRef<() => Promise<void>>(async () => {});
  const sincronizar = useCallback(() => sincronizarRef.current(), []);

  // Realtime para novas mensagens (INSERT, UPDATE e DELETE)
  useEffect(() => {
    if (!conversationId) return;

    let canal: any = null;
    let vivo = true;

    // Busca o que chegou enquanto o WS esteve morto (gap apos restart do
    // backend) e mescla sem duplicar. Sem isso, msg perdida so aparecia no F5.
    const catchUp = async () => {
      const { data } = await supabase
        .from('whatsapp_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .gt('created_at', newestCreatedAt.current || '1970-01-01')
        .order('created_at', { ascending: true })
        .limit(PAGE_SIZE);
      const novas = (data || []) as Message[];
      if (!novas.length) return;
      setMessages((prev) => {
        const ids = new Set(prev.map((m) => m.id));
        const add = novas.filter((m) => !ids.has(m.id));
        return add.length ? [...prev, ...add] : prev;
      });
      const ult = novas[novas.length - 1];
      if (ult && ult.created_at > (newestCreatedAt.current || '')) {
        newestCreatedAt.current = ult.created_at;
      }
    };

    sincronizarRef.current = catchUp;
    let jaEntrou = false;
    const assinar = () => {
      if (!vivo) return;
      if (canal) { try { supabase.removeChannel(canal); } catch { /* noop */ } }
      jaEntrou = false;
      canal = supabase
      .channel(`msgs-rt-${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'whatsapp_messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const newMsg = payload.new as Message;
          setMessages((prev) => {
            if (prev.some((m) => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });
          if (newMsg.created_at > (newestCreatedAt.current || '')) {
            newestCreatedAt.current = newMsg.created_at;
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'whatsapp_messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const updated = payload.new as Message;
          setMessages((prev) =>
            prev.map((m) => (m.id === updated.id ? updated : m))
          );
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'whatsapp_messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const deletedId = (payload.old as any)?.id;
          if (deletedId) {
            setMessages((prev) => prev.filter((m) => m.id !== deletedId));
          }
        }
      )
      .subscribe((status: string) => {
        if (status !== 'SUBSCRIBED') return;
        if (jaEntrou) void catchUp();
        jaEntrou = true;
      });
    };
    assinar();

    // WS cai quando o backend reinicia e o canal NAO volta sozinho — watchdog
    // re-assina e faz catch-up do gap. Volta pra aba tambem re-sincroniza.
    const watchdog = setInterval(() => {
      const st = String(canal?.state || '');
      if (st !== 'joined' && st !== 'joining') { assinar(); void catchUp(); }
    }, 10_000);
    const onVis = () => { if (document.visibilityState === 'visible') void catchUp(); };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      vivo = false;
      sincronizarRef.current = async () => {};
      clearInterval(watchdog);
      document.removeEventListener('visibilitychange', onVis);
      if (canal) supabase.removeChannel(canal);
    };
  }, [conversationId]);

  const visiveis = useMemo(() => messages.filter((m) => !ehOrdemDaMaquina(m)), [messages]);
  return { messages: visiveis, isLoading, hasMore, loadMore, targetError, sincronizar };
}
